'use client'

import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import CriteriaOverview, {
  CriteriaOverviewContent,
  type CriterionWithRating,
} from './CriteriaOverview'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { extractUniqueCriteriaFromApp } from '@/lib/formatting'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { App, Category, CategoryCriteriaCriterion } from '@/payload-types'

type CriteriaIconsProps = {
  app: Pick<
    App,
    | 'id'
    | 'name'
    | 'slug'
    | 'icon'
    | 'glyph'
    | 'brandBackgroundColor'
    | 'brandPrimaryColor'
    | 'categoryRecommendations'
  >
  color?: string
  categoryId?: Category['id']
  reviewCriterionRatings?: Array<{
    categoryCriterion?: CategoryCriteriaCriterion['id'] | CategoryCriteriaCriterion | null
    rating?: number | null
  }>
  horizontalAlign?: 'left' | 'center' | 'right'
}

export default function CriteriaIcons({
  app,
  color,
  categoryId,
  reviewCriterionRatings,
  horizontalAlign = 'center',
}: CriteriaIconsProps) {
  const { md } = useBreakpoint()
  const [criterion, setCriterion] = useState<CriterionWithRating | null>(null)
  const criteria = useMemo<CriterionWithRating[]>(
    () => extractUniqueCriteriaFromApp(app, reviewCriterionRatings, categoryId),
    [app, reviewCriterionRatings, categoryId],
  )

  const allCriteriaRated =
    criteria.length > 0 &&
    criteria.every((c) => c.isApplicable === false || (c.rating !== null && c.rating !== undefined))

  if (!allCriteriaRated) {
    return null
  }

  if (!md) {
    return (
      <CriteriaOverview app={app} criteria={criteria} verticalAlign="top">
        <div className={cn('relative flex gap-5 text-12')}>
          {criteria.map((c) => {
            if (!c) return null
            const rating = c.rating ?? 0
            const isActive = criterion?.id === c.id

            return (
              <div key={c.id} className="h-4.5 cursor-default">
                <Icon
                  name={c.icon! ?? 'circle-question'}
                  className={cn(
                    'transition-colors',
                    color ??
                      (isActive
                        ? rating >= 5
                          ? 'text-primary-500'
                          : 'text-white'
                        : rating >= 5
                          ? 'text-white hover:text-primary-500'
                          : 'text-white/30 hover:text-white'),
                  )}
                />
              </div>
            )
          })}
        </div>
      </CriteriaOverview>
    )
  }

  const xPosition = horizontalAlign === 'center' ? '-50%' : '0'

  return (
    <div
      className={cn('relative flex w-fit gap-5 text-12')}
      onMouseLeave={() => setCriterion(null)}
    >
      {criteria.map((c, index) => {
        if (!c) return null
        const rating = c.rating ?? 0
        return (
          <div
            key={`${c.name}-${c.icon}-${index}`}
            onMouseEnter={() => setCriterion(c)}
            className="h-4.5 cursor-default"
          >
            <Icon
              name={c.icon! ?? 'circle-question'}
              className={cn(
                'transition-colors',
                color ??
                  (criterion?.id === c.id
                    ? rating >= 5
                      ? 'text-primary-500'
                      : 'text-white'
                    : rating >= 5
                      ? 'text-white hover:text-primary-500'
                      : 'text-white/30 hover:text-white'),
              )}
            />
          </div>
        )
      })}

      {/* Desktop hover overlay */}
      <AnimatePresence>
        {criterion && (
          <motion.div
            initial={{ opacity: 0, y: 10, x: xPosition }}
            animate={{ opacity: 1, y: 0, x: xPosition }}
            exit={{ opacity: 0, y: 10, x: xPosition }}
            className={cn(
              'absolute top-full pt-4',
              horizontalAlign === 'left' && 'left-0',
              horizontalAlign === 'right' && 'right-0',
              horizontalAlign === 'center' && 'left-1/2',
            )}
          >
            <CriteriaOverviewContent app={app} criteria={[criterion]} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
